import { serializeParams } from "@/utils/apiUtils";
import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../common/api/ApiRequestAdapter";
import { apiService } from "../../common/api/apiService";
import { HttpContentType, HttpHeaders, HttpMethod } from "../../common/api/http";
import { PageSizes } from "../../common/constants";
import {
  EntityIdObject,
  EntityObject,
  PageRequest,
  PageResult,
  SearchPageRequest,
  SearchPageResult,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../common/types";
import {
  Agent,
  AgentAttachment,
  AgentBranding,
  AgentBrokerNumber,
  AgentCompetence,
  AgentCompetenceFilterPageRequest,
  AgentCompetenceFilterPageResult,
  AgentCompletedEducation,
  AgentCompletedEducationDetail,
  AgentDirectorate,
  AgentEducation,
  AgentEducationFilterPageRequest,
  AgentEducationFilterPageResult,
  AgentFilterExportRequest,
  AgentFilterPageRequest,
  AgentFilterPageResult,
  AgentLicense,
  AgentMentoring,
  AgentMentoringFilterPageRequest,
  AgentPartnerIdNumberRequest,
  AgentProfilePicture,
  AgentSearchRequest,
  AgentSearchResult,
  AgentTree,
  AgentTreeFilterRequest,
  CommonAgentFilterRequest,
  CreateUpdateAgent,
  CreateUpdateAgentBranding,
  CreateUpdateAgentBrokerNumber,
  CreateUpdateAgentCompetence,
  CreateUpdateAgentCompletedEducation,
  CreateUpdateAgentDirectorate,
  CreateUpdateAgentEducation,
  CreateUpdateAgentLicense,
  CreateUpdateAgentMentoring,
  CreateUpdateSecondaryAgent,
  SecondaryAgent,
  UpdateAgentCompletedEducationDetail,
  UpdateAgentParent,
} from "./types";

const FILTER_AGENTS = ApiRequestAdapter.create("/agents", HttpMethod.GET);
const DOWNLOAD_AGENTS_EXPORT = ApiRequestAdapter.create("/agents/export", HttpMethod.GET);
const DOWNLOAD_SECONDARY_AGENTS_EXPORT = ApiRequestAdapter.create("/agents/secondary-agents-export", HttpMethod.GET);
const GET_AGENT_TREE = ApiRequestAdapter.create("/agents/tree", HttpMethod.GET);
const SEARCH_AGENT = ApiRequestAdapter.create("/agents/search", HttpMethod.GET);
const GET_AGENT = ApiRequestAdapter.create("/agents/{0}", HttpMethod.GET);
const CREATE_AGENT = ApiRequestAdapter.create("/agents", HttpMethod.POST);
const UPDATE_AGENT = ApiRequestAdapter.create("/agents/{0}", HttpMethod.PUT);
const UPDATE_AGENT_PARENT = ApiRequestAdapter.create("/agents/{0}/parent", HttpMethod.PUT);
const DELETE_AGENT = ApiRequestAdapter.create("/agents/{0}", HttpMethod.DELETE);

const DOWNLOAD_PROFILE_PICTURE = ApiRequestAdapter.create("/agents/{0}/profile-picture", HttpMethod.GET);
const UPDATE_PROFILE_PICTURE = ApiRequestAdapter.create("/agents/{0}/profile-picture", HttpMethod.PUT);
const DELETE_PROFILE_PICTURE = ApiRequestAdapter.create("/agents/{0}/profile-picture", HttpMethod.DELETE);

const UPLOAD_AGENT_ATTACHMENTS = ApiRequestAdapter.create("/agents/{0}/attachments", HttpMethod.POST);
const DOWNLOAD_AGENT_ATTACHMENT = ApiRequestAdapter.create("/agents/{0}/attachments/{1}", HttpMethod.GET);
const DOWNLOAD_AGENT_ATTACHMENTS_AS_ZIP = ApiRequestAdapter.create("/agents/{0}/attachments/all", HttpMethod.GET);
const DELETE_AGENT_ATTACHMENT = ApiRequestAdapter.create("/agents/{0}/attachments/{1}", HttpMethod.DELETE);

const CREATE_AGENT_BRANDING = ApiRequestAdapter.create("/agents/{0}/branding", HttpMethod.POST);
const GET_AGENT_BRANDING = ApiRequestAdapter.create("/agents/{0}/branding", HttpMethod.GET);
const UPDATE_AGENT_BRANDING = ApiRequestAdapter.create("/agents/{0}/branding/{1}", HttpMethod.PUT);
const DELETE_AGENT_BRANDING = ApiRequestAdapter.create("/agents/{0}/branding/{1}", HttpMethod.DELETE);

const FILTER_AGENT_ENDING_COMPETENCES = ApiRequestAdapter.create("/agents/ending-competences", HttpMethod.POST);
const GET_AGENT_COMPETENCES = ApiRequestAdapter.create("/agents/{0}/competences", HttpMethod.GET);
const CREATE_AGENT_COMPETENCE = ApiRequestAdapter.create("/agents/{0}/competences", HttpMethod.POST);
const UPDATE_AGENT_COMPETENCE = ApiRequestAdapter.create("/agents/{0}/competences/{1}", HttpMethod.PUT);
const DELETE_AGENT_COMPETENCE = ApiRequestAdapter.create("/agents/{0}/competences/{1}", HttpMethod.DELETE);

const GET_AGENT_LICENSES = ApiRequestAdapter.create("/agents/{0}/licenses", HttpMethod.GET);
const CREATE_AGENT_LICENSE = ApiRequestAdapter.create("/agents/{0}/licenses", HttpMethod.POST);
const UPDATE_AGENT_LICENSE = ApiRequestAdapter.create("/agents/{0}/licenses/{1}", HttpMethod.PUT);
const DELETE_AGENT_LICENSE = ApiRequestAdapter.create("/agents/{0}/licenses/{1}", HttpMethod.DELETE);

const GET_AGENT_BROKER_NUMBERS = ApiRequestAdapter.create("/agents/{0}/broker-numbers", HttpMethod.GET);
const CREATE_AGENT_BROKER_NUMBER = ApiRequestAdapter.create("/agents/{0}/broker-numbers", HttpMethod.POST);
const UPDATE_AGENT_BROKER_NUMBER = ApiRequestAdapter.create("/agents/{0}/broker-numbers/{1}", HttpMethod.PUT);
const DELETE_AGENT_BROKER_NUMBER = ApiRequestAdapter.create("/agents/{0}/broker-numbers/{1}", HttpMethod.DELETE);

const FILTER_AGENT_DIRECTORATES = ApiRequestAdapter.create("/agent-directorates", HttpMethod.GET);
const CREATE_AGENT_DIRECTORATE = ApiRequestAdapter.create("/agent-directorates", HttpMethod.POST);
const UPDATE_AGENT_DIRECTORATE = ApiRequestAdapter.create("/agent-directorates/{0}", HttpMethod.PUT);
const DELETE_AGENT_DIRECTORATE = ApiRequestAdapter.create("/agent-directorates/{0}", HttpMethod.DELETE);

const FILTER_AGENT_EDUCATIONS = ApiRequestAdapter.create("/agent-educations", HttpMethod.GET);
const CREATE_AGENT_EDUCATION = ApiRequestAdapter.create("/agent-educations", HttpMethod.POST);
const UPDATE_AGENT_EDUCATION = ApiRequestAdapter.create("/agent-educations/{0}", HttpMethod.PUT);
const DELETE_AGENT_EDUCATION = ApiRequestAdapter.create("/agent-educations/{0}", HttpMethod.DELETE);

const GET_AGENT_COMPLETED_EDUCATIONS = ApiRequestAdapter.create("/agents/{0}/educations", HttpMethod.GET);
const CREATE_AGENT_COMPLETED_EDUCATION = ApiRequestAdapter.create("/agents/{0}/educations", HttpMethod.POST);
const UPDATE_AGENT_COMPLETED_EDUCATION = ApiRequestAdapter.create("/agents/{0}/educations/{1}", HttpMethod.PUT);
const DELETE_AGENT_COMPLETED_EDUCATION = ApiRequestAdapter.create("/agents/{0}/educations/{1}", HttpMethod.DELETE);
const GET_AGENT_COMPLETED_EDUCATION_DETAIL = ApiRequestAdapter.create("/agents/{0}/education-details", HttpMethod.GET);
const UPDATE_AGENT_COMPLETED_EDUCATION_DETAIL = ApiRequestAdapter.create(
  "/agents/{0}/education-details",
  HttpMethod.PUT,
);

const FILTER_AGENT_MENTORINGS = ApiRequestAdapter.create("/agent-mentorings", HttpMethod.GET);
const CREATE_AGENT_MENTORING = ApiRequestAdapter.create("/agent-mentorings", HttpMethod.POST);
const UPDATE_AGENT_MENTORING = ApiRequestAdapter.create("/agent-mentorings/{0}", HttpMethod.PUT);
const DELETE_AGENT_MENTORING = ApiRequestAdapter.create("/agent-mentorings/{0}", HttpMethod.DELETE);

const GET_AGENT_PARTNER_ID_NUMBER = ApiRequestAdapter.create("/agents/partner-id-number", HttpMethod.GET);
const IMPORT_AGENTS = ApiRequestAdapter.create("/agents/import", HttpMethod.POST);
const DOWNLOAD_AGENTS_IMPORT_RESULT = ApiRequestAdapter.create("/agents/import/{0}", HttpMethod.GET);

const GET_SECONDARY_AGENT = ApiRequestAdapter.create("/agents/{0}/secondary-agents", HttpMethod.GET);
const CREATE_SECONDARY_AGENT = ApiRequestAdapter.create("/agents/{0}/secondary-agents", HttpMethod.POST);
const UPDATE_SECONDARY_AGENT = ApiRequestAdapter.create("/agents/{0}/secondary-agents/{1}", HttpMethod.PUT);
const DELETE_SECONDARY_AGENT = ApiRequestAdapter.create("/agents/{0}/secondary-agents/{1}", HttpMethod.DELETE);

export const agentApi = {
  filterAgents: (request: AgentFilterPageRequest): AxiosPromise<AgentFilterPageResult> => {
    return apiService.get(FILTER_AGENTS.url, { params: request, paramsSerializer: { serialize: serializeParams } });
  },
  downloadAgentsExport: (request: AgentFilterExportRequest): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_AGENTS_EXPORT.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
      responseType: "blob",
    });
  },
  downloadSecondaryAgentsExport: (request: AgentFilterExportRequest): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_SECONDARY_AGENTS_EXPORT.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
      responseType: "blob",
    });
  },
  getAgentTree: (request: AgentTreeFilterRequest): AxiosPromise<AgentTree> => {
    return apiService.get(GET_AGENT_TREE.url, { params: request });
  },
  searchAgent: (request: AgentSearchRequest): AxiosPromise<AgentSearchResult> => {
    return apiService.get(SEARCH_AGENT.url, { params: request });
  },
  getAgent: (request: EntityIdObject): AxiosPromise<Agent> => {
    return apiService.get(GET_AGENT.getParametrizedUrl(request.id));
  },
  createAgent: (request: CreateUpdateAgent): AxiosPromise<Agent> => {
    return apiService.post(CREATE_AGENT.url, request);
  },
  getAgentPartnerIdNumber: (request?: AgentPartnerIdNumberRequest): AxiosPromise<string> => {
    return apiService.get(GET_AGENT_PARTNER_ID_NUMBER.url, {
      params: request,
      transformResponse: (data) => (data === "null" ? "" : data),
    });
  },
  updateAgent: (request: EntityObject<CreateUpdateAgent>): AxiosPromise<Agent> => {
    return apiService.put(UPDATE_AGENT.getParametrizedUrl(request.id), request.object);
  },
  updateAgentParent: (request: EntityObject<UpdateAgentParent>): AxiosPromise<Agent> => {
    return apiService.put(UPDATE_AGENT_PARENT.getParametrizedUrl(request.id), request.object);
  },
  deleteAgent: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_AGENT.getParametrizedUrl(request.id));
  },
  downloadProfilePicture: (request: EntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_PROFILE_PICTURE.getParametrizedUrl(request.id), { responseType: "blob" });
  },
  updateProfilePicture: (request: EntityObject<FormData>): AxiosPromise<AgentProfilePicture> => {
    return apiService.put(UPDATE_PROFILE_PICTURE.getParametrizedUrl(request.id), request.object, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  deleteProfilePicture: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_PROFILE_PICTURE.getParametrizedUrl(request.id));
  },
  uploadAgentAttachments: (request: EntityObject<FormData>): AxiosPromise<AgentAttachment[]> => {
    return apiService.post(UPLOAD_AGENT_ATTACHMENTS.getParametrizedUrl(request.id), request.object, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  downloadAgentAttachment: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_AGENT_ATTACHMENT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob",
    });
  },
  downloadAgentAttachmentsAsZip: (request: EntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_AGENT_ATTACHMENTS_AS_ZIP.getParametrizedUrl(request.id), {
      responseType: "blob",
    });
  },
  deleteAgentAttachment: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_AGENT_ATTACHMENT.getParametrizedUrl(request.id1, request.id2));
  },
  createAgentBranding: (request: EntityObject<CreateUpdateAgentBranding>): AxiosPromise<AgentBranding> => {
    return apiService.post(CREATE_AGENT_BRANDING.getParametrizedUrl(request.id), request.object);
  },
  getAgentBranding: (request: EntityIdObject): AxiosPromise<AgentBranding> => {
    return apiService.get(GET_AGENT_BRANDING.getParametrizedUrl(request.id));
  },
  updateAgentBranding: (request: TwoLevelEntityObject<CreateUpdateAgentBranding>): AxiosPromise<AgentBranding> => {
    return apiService.put(UPDATE_AGENT_BRANDING.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteAgentBranding: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_AGENT_BRANDING.getParametrizedUrl(request.id1, request.id2));
  },
  filterAgentEndingCompetences: (
    request: AgentCompetenceFilterPageRequest,
  ): AxiosPromise<AgentCompetenceFilterPageResult> => {
    return apiService.post(FILTER_AGENT_ENDING_COMPETENCES.url, request);
  },
  getAgentCompetences: (request: EntityIdObject): AxiosPromise<AgentCompetence[]> => {
    return apiService.get(GET_AGENT_COMPETENCES.getParametrizedUrl(request.id));
  },
  createAgentCompetence: (request: EntityObject<CreateUpdateAgentCompetence>): AxiosPromise<AgentCompetence> => {
    return apiService.post(CREATE_AGENT_COMPETENCE.getParametrizedUrl(request.id), request.object);
  },
  updateAgentCompetence: (
    request: TwoLevelEntityObject<CreateUpdateAgentCompetence>,
  ): AxiosPromise<AgentCompetence> => {
    return apiService.put(UPDATE_AGENT_COMPETENCE.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteAgentCompetence: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_AGENT_COMPETENCE.getParametrizedUrl(request.id1, request.id2));
  },
  getAgentLicenses: (request: EntityIdObject): AxiosPromise<AgentLicense[]> => {
    return apiService.get(GET_AGENT_LICENSES.getParametrizedUrl(request.id));
  },
  createAgentLicense: (request: EntityObject<CreateUpdateAgentLicense>): AxiosPromise<AgentLicense> => {
    return apiService.post(CREATE_AGENT_LICENSE.getParametrizedUrl(request.id), request.object);
  },
  updateAgentLicense: (request: TwoLevelEntityObject<CreateUpdateAgentLicense>): AxiosPromise<AgentLicense> => {
    return apiService.put(UPDATE_AGENT_LICENSE.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteAgentLicense: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_AGENT_LICENSE.getParametrizedUrl(request.id1, request.id2));
  },
  getAgentBrokerNumbers: (request: EntityIdObject): AxiosPromise<AgentBrokerNumber[]> => {
    return apiService.get(GET_AGENT_BROKER_NUMBERS.getParametrizedUrl(request.id));
  },
  createAgentBrokerNumber: (request: EntityObject<CreateUpdateAgentBrokerNumber>): AxiosPromise<AgentBrokerNumber> => {
    return apiService.post(CREATE_AGENT_BROKER_NUMBER.getParametrizedUrl(request.id), request.object);
  },
  updateAgentBrokerNumber: (
    request: TwoLevelEntityObject<CreateUpdateAgentBrokerNumber>,
  ): AxiosPromise<AgentBrokerNumber> => {
    return apiService.put(UPDATE_AGENT_BROKER_NUMBER.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteAgentBrokerNumber: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_AGENT_BROKER_NUMBER.getParametrizedUrl(request.id1, request.id2));
  },
  filterAgentDirectorates: (request: SearchPageRequest): AxiosPromise<SearchPageResult<AgentDirectorate>> => {
    return apiService.get(FILTER_AGENT_DIRECTORATES.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  createAgentDirectorate: (request: CreateUpdateAgentDirectorate): AxiosPromise<AgentDirectorate> => {
    return apiService.post(CREATE_AGENT_DIRECTORATE.url, request);
  },
  updateAgentDirectorate: (request: EntityObject<CreateUpdateAgentDirectorate>): AxiosPromise<AgentDirectorate> => {
    return apiService.put(UPDATE_AGENT_DIRECTORATE.getParametrizedUrl(request.id), request.object);
  },
  deleteAgentDirectorate: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_AGENT_DIRECTORATE.getParametrizedUrl(request.id));
  },
  filterAgentEducations: (request: AgentEducationFilterPageRequest): AxiosPromise<AgentEducationFilterPageResult> => {
    return apiService.get(FILTER_AGENT_EDUCATIONS.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  createAgentEducation: (request: CreateUpdateAgentEducation): AxiosPromise<AgentEducation> => {
    return apiService.post(CREATE_AGENT_EDUCATION.url, request);
  },
  updateAgentEducation: (request: EntityObject<CreateUpdateAgentEducation>): AxiosPromise<AgentEducation> => {
    return apiService.put(UPDATE_AGENT_EDUCATION.getParametrizedUrl(request.id), request.object);
  },
  deleteAgentEducation: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_AGENT_EDUCATION.getParametrizedUrl(request.id));
  },
  getAgentCompletedEducations: (
    request: EntityObject<PageRequest>,
  ): AxiosPromise<PageResult<AgentCompletedEducation>> => {
    return apiService.get(GET_AGENT_COMPLETED_EDUCATIONS.getParametrizedUrl(request.id), { params: request.object });
  },
  createAgentCompletedEducation: (
    request: EntityObject<CreateUpdateAgentCompletedEducation>,
  ): AxiosPromise<AgentCompletedEducation> => {
    return apiService.post(CREATE_AGENT_COMPLETED_EDUCATION.getParametrizedUrl(request.id), request.object);
  },
  updateAgentCompletedEducation: (
    request: TwoLevelEntityObject<CreateUpdateAgentCompletedEducation>,
  ): AxiosPromise<AgentCompletedEducation> => {
    return apiService.put(
      UPDATE_AGENT_COMPLETED_EDUCATION.getParametrizedUrl(request.id1, request.id2),
      request.object,
    );
  },
  deleteAgentCompletedEducation: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_AGENT_COMPLETED_EDUCATION.getParametrizedUrl(request.id1, request.id2));
  },
  filterAgentMentorings: (request: AgentMentoringFilterPageRequest): AxiosPromise<PageResult<AgentMentoring>> => {
    return apiService.get(FILTER_AGENT_MENTORINGS.url, { params: request });
  },
  createAgentMentoring: (request: CreateUpdateAgentMentoring): AxiosPromise<AgentMentoring> => {
    return apiService.post(CREATE_AGENT_MENTORING.url, request);
  },
  updateAgentMentoring: (request: EntityObject<CreateUpdateAgentMentoring>): AxiosPromise<AgentMentoring> => {
    return apiService.put(UPDATE_AGENT_MENTORING.getParametrizedUrl(request.id), request.object);
  },
  deleteAgentMentoring: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_AGENT_MENTORING.getParametrizedUrl(request.id));
  },
  importAgents: (request: FormData): AxiosPromise<number> => {
    return apiService.post(IMPORT_AGENTS.url, request, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  downloadAgentsImportResult: (request: EntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_AGENTS_IMPORT_RESULT.getParametrizedUrl(request.id), { responseType: "blob" });
  },
  getAgentCompletedEducationDetail: (request: EntityIdObject): AxiosPromise<AgentCompletedEducationDetail> => {
    return apiService.get(GET_AGENT_COMPLETED_EDUCATION_DETAIL.getParametrizedUrl(request.id));
  },
  updateAgentCompletedEducationDetail: (
    request: EntityObject<UpdateAgentCompletedEducationDetail>,
  ): AxiosPromise<AgentCompletedEducationDetail> => {
    return apiService.put(UPDATE_AGENT_COMPLETED_EDUCATION_DETAIL.getParametrizedUrl(request.id), request.object);
  },
  getSecondaryAgent: (request: EntityIdObject): AxiosPromise<SecondaryAgent> => {
    return apiService.get(GET_SECONDARY_AGENT.getParametrizedUrl(request.id));
  },
  createSecondaryAgent: (request: EntityObject<CreateUpdateSecondaryAgent>): AxiosPromise<SecondaryAgent> => {
    return apiService.post(CREATE_SECONDARY_AGENT.getParametrizedUrl(request.id), request.object);
  },
  updateSecondaryAgent: (request: TwoLevelEntityObject<CreateUpdateSecondaryAgent>): AxiosPromise<SecondaryAgent> => {
    return apiService.put(UPDATE_SECONDARY_AGENT.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteSecondaryAgent: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_SECONDARY_AGENT.getParametrizedUrl(request.id1, request.id2));
  },
};

export const DEFAULT_FILTER_AGENT_TREE_QUERY_PARAMS: Required<AgentTreeFilterRequest> = {
  rootAgentId: "",
  includeDeactivated: true,
  includeRepresentatives: false,
};

/**
 * Inclusion flag defaults, mirroring the backend `AgentFilterInclusionDefaults`. Each listing must
 * use the preset of the endpoint it calls — otherwise `skipInitialFormValues` drops the flag from
 * the URL and the checkbox shows the opposite of what the server applied.
 */
export const AGENT_FILTER_INCLUSION_DEFAULTS = {
  ALL_INCLUDED: {
    includeDeactivated: true,
    includeRepresentatives: true,
    includeNonGainers: true,
  },
  NO_REPRESENTATIVES: {
    includeDeactivated: true,
    includeRepresentatives: false,
    includeNonGainers: true,
  },
  NONE_INCLUDED: {
    includeDeactivated: false,
    includeRepresentatives: false,
    includeNonGainers: false,
  },
} as const;

/** Combine with one of the {@link AGENT_FILTER_INCLUSION_DEFAULTS} presets to get the whole shared filter. */
export const COMMON_AGENT_FILTER_QUERY_PARAMS: Omit<
  Required<CommonAgentFilterRequest>,
  "includeDeactivated" | "includeRepresentatives" | "includeNonGainers"
> = {
  pageIndex: 0,
  pageSize: PageSizes.LARGE,
  keyword: "",
  commissionsSettingsLevelIds: [],
  commissionsSettingsRankIds: [],
  commissionsSettingsStatuses: [],
  statuses: [],
  secondaryStatuses: [],
  directorateIds: [],
  completedEducationIds: [],
  completedEducationDateMin: "",
  completedEducationDateMax: "",
  completedAcademies: [],
  completedAcademyDateMin: "",
  completedAcademyDateMax: "",
  agentCreatedAtDateMin: "",
  rootAgentId: "",
  onlyDirectSubordinates: false,
  onlyRealtySpecialists: false,
  onlyLoanSpecialists: false,
};

/**
 * Spread into the `params` option of `useFilterSearchParams`. `keyword` is omitted on purpose — the
 * hook reads it on its own.
 */
export const COMMON_AGENT_FILTER_SEARCH_PARAMS = [
  "commissionsSettingsLevelIds",
  "commissionsSettingsRankIds",
  "commissionsSettingsStatuses",
  "statuses",
  "secondaryStatuses",
  "directorateIds",
  "completedEducationIds",
  "completedEducationDateMin",
  "completedEducationDateMax",
  "completedAcademies",
  "completedAcademyDateMin",
  "completedAcademyDateMax",
  "agentCreatedAtDateMin",
  "rootAgentId",
  "onlyDirectSubordinates",
  "onlyRealtySpecialists",
  "onlyLoanSpecialists",
  "includeDeactivated",
  "includeRepresentatives",
  "includeNonGainers",
] as const satisfies readonly (keyof CommonAgentFilterRequest)[];

export const DEFAULT_FILTER_AGENTS_QUERY_PARAMS: Required<AgentFilterPageRequest> = {
  ...COMMON_AGENT_FILTER_QUERY_PARAMS,
  ...AGENT_FILTER_INCLUSION_DEFAULTS.ALL_INCLUDED,
};

export const DEFAULT_FILTER_AGENT_COMPETENCES_QUERY_PARAMS: Required<AgentCompetenceFilterPageRequest> = {
  ...COMMON_AGENT_FILTER_QUERY_PARAMS,
  ...AGENT_FILTER_INCLUSION_DEFAULTS.ALL_INCLUDED,
  types: [],
  sectors: [],
  levels: [],
  providers: [],
};
